import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getAds, saveAd, deleteAd } from "../Apis/AdAPIs";
import { uploadFile } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";
import { toast } from "react-toastify";
import usePermissions from "../hook/userPermission";
import StandardDatePicker from "./StandardDatePicker";

const AdLayer = () => {
  const { hasPermission } = usePermissions();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    expireDate: "",
    image: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  const fetchAds = async () => {
    setLoading(true);
    const res = await getAds();
    if (res.status) {
      setAds(res.response?.data || res.response || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeSelectedFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDateChange = (e) => {
    setFormData(prev => ({ ...prev, expireDate: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error("Please select an ad image to upload");
      return;
    }

    if (!formData.expireDate) {
      toast.error("Please specify an expiry date");
      return;
    }

    setSubmitting(true);

    try {
      // 1. Upload File
      const uploadFormData = new FormData();
      uploadFormData.append("file", selectedFile);
      uploadFormData.append("folder", "mobile-banners");

      const uploadRes = await uploadFile(uploadFormData);

      if (!uploadRes.status) {
        toast.error("Failed to upload the ad image");
        setSubmitting(false);
        return;
      }

      const imagePath = uploadRes.response?.path || uploadRes.response?.data?.path || uploadRes.response;

      // 2. Save Ad
      const res = await saveAd({
        image: imagePath,
        expireDate: formData.expireDate,
        expiryDate: formData.expireDate, // Include both for safety/compatibility
      });

      if (res.status) {
        toast.success("Flash news saved successfully");
        resetForm();
        fetchAds();
      }
    } catch (error) {
      console.error("Error saving mobile ad:", error);
      toast.error("An error occurred while saving the ad");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ expireDate: "", image: "" });
    setSelectedFile(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      const res = await deleteAd(deleteId);
      if (res.status) {
        toast.success("Flash news deleted successfully");
        fetchAds();
      }
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm bg-light-50">
      {/* Custom Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, backdropFilter: 'blur(4px)' }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
            <div className="modal-content radius-16 border-0 shadow-2xl overflow-hidden">
              <div className="modal-body p-32 text-center">
                <div
                  className="bg-danger-50 w-80-px h-80-px radius-circle d-flex align-items-center justify-content-center mx-auto mb-24 border border-danger-100"
                >
                  <Icon icon="solar:trash-bin-2-bold-duotone" className="text-4xl text-danger" />
                </div>
                <h5 className="text-dark fw-bold mb-12">Delete Ad?</h5>
                <p className="text-secondary-light mb-32 px-12">
                  This action cannot be undone. Are you sure you want to remove this mobile banner ad?
                </p>
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-light radius-12 py-12 px-24 flex-grow-1 fw-bold text-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    CANCEL
                  </button>
                  <button
                    className="btn btn-danger radius-12 py-12 px-24 flex-grow-1 fw-bold shadow-sm"
                    onClick={confirmDelete}
                  >
                    DELETE NOW
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Card Header matching theme style */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
        style={{
          background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)"
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <Icon icon="solar:videocards-bold" className="text-white text-xl" />
          <h6 className="mb-0 fw-bold uppercase text-white pb-2" style={{ fontWeight: '700' }}>Flash News</h6>
        </div>
      </div>

      <div className="card-body p-24">
        {/* Form Container */}
        {hasPermission("Banner Ads", "add") && (
          <div className="bg-white p-24 radius-16 shadow-sm mb-32 border border-light-200">
            <form onSubmit={handleSubmit} className="row g-4">
              {/* Custom choose file input matching screenshot exactly */}
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "14px" }}>
                    Add Image <span className="text-danger">*</span>
                  </label>
                  <div
                    onClick={triggerFileInput}
                    className="form-control radius-8 py-10 px-16 text-sm d-flex align-items-center justify-content-between cursor-pointer border-light-200"
                    style={{ backgroundColor: '#fff', height: '45px', border: '1px solid #ced4da' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm py-4 px-12 border-0 radius-4"
                        style={{ backgroundColor: "#f1f5f9", fontSize: "12px", border: "1px solid #cbd5e1" }}
                      >
                        Choose file
                      </button>
                      <span className="text-secondary text-truncate" style={{ fontSize: "13px", maxWidth: "220px" }}>
                        {selectedFile ? selectedFile.name : "No file chosen"}
                      </span>
                    </div>
                    {previewUrl && (
                      <button
                        type="button"
                        onClick={removeSelectedFile}
                        className="btn p-0 border-0 text-danger hover-text-danger-600 d-flex align-items-center justify-content-center"
                        style={{ width: '20px', height: '20px' }}
                      >
                        <Icon icon="solar:close-circle-bold" className="text-lg" />
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </div>
              </div>

              {/* Expiry Date */}
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label fw-bold text-dark text-uppercase mb-8 d-flex align-items-center gap-1" style={{ fontSize: "14px" }}>
                    Expiry Date <span className="text-danger">*</span>
                  </label>
                  <StandardDatePicker
                    name="expireDate"
                    value={formData.expireDate}
                    onChange={handleDateChange}
                    minDate="today"
                    placeholder="dd-mm-yyyy"
                  />
                  <div className="text-secondary mt-4" style={{ fontSize: '11px' }}>
                    Banner will be automatically hidden after this date.
                  </div>
                </div>
              </div>

              {/* Preview Box if image is chosen */}
              {previewUrl && (
                <div className="col-12 mt-8">
                  <div className="border radius-12 p-12 bg-light-50 d-inline-block">
                    <div className="text-xs fw-bold text-secondary mb-8 text-uppercase">Image Preview:</div>
                    <img
                      src={previewUrl}
                      alt="Selected preview"
                      className="radius-8 object-fit-contain border"
                      style={{ maxHeight: '120px', maxWidth: '300px' }}
                    />
                  </div>
                </div>
              )}

              {/* Save Button right-aligned */}
              <div className="col-12 d-flex justify-content-end align-items-center mt-16">
                <button
                  type="submit"
                  className="btn px-48 py-11 fw-bold text-white shadow-sm border-0 transition-all hover-scale-105"
                  style={{
                    backgroundColor: "#ed1c24", // Theme red button
                    fontWeight: "700",
                    borderRadius: "8px"
                  }}
                  disabled={submitting || !selectedFile || !formData.expireDate}
                >
                  {submitting ? (
                    <span className="spinner-border spinner-border-sm" role="status"></span>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Gallery Section */}
        <div className="row g-4 mt-8">
          {ads.map((ad) => (
            <div key={ad._id || ad.id} className="col-12 col-xl-4 col-md-6">
              <div className="ad-preview-card radius-16 overflow-hidden position-relative shadow-sm bg-white border h-100 transition-all hover-shadow-md d-flex flex-column">

                {/* Image display */}
                <div className="position-relative bg-light-50 border-bottom d-flex align-items-center justify-content-center" style={{ height: '220px', overflow: 'hidden' }}>
                  <img
                    src={getImageUrl(ad.image)}
                    alt="Mobile banner ad"
                    className="w-100 h-100 object-fit-cover transition-all"
                  />
                  {/* Overlay Badge for expiry status */}
                  <div className="position-absolute top-0 start-0 m-12">
                    <div
                      className="badge text-white px-8 py-4 radius-4 fw-bold uppercase shadow-sm d-flex align-items-center gap-1"
                      style={{
                        fontSize: '9px',
                        letterSpacing: '0.5px',
                        backgroundColor: "rgba(237, 28, 36, 0.9)"
                      }}
                    >
                      <Icon icon="solar:clock-circle-bold" />
                      Expires: {formatDisplayDate(ad.expireDate || ad.expiryDate)}
                    </div>
                  </div>
                </div>

                {/* Footer Section with Delete Button */}
                <div className="p-16 d-flex align-items-center justify-content-end bg-white">
                  {hasPermission("Banner Ads", "delete") && (
                    <button
                      onClick={() => handleDeleteClick(ad._id || ad.id)}
                      className="btn py-8 px-16 fw-bold text-white d-flex align-items-center gap-2 transition-all hover-scale-105 border-0"
                      style={{
                        backgroundColor: "#ed1c24", // Theme red button
                        fontWeight: "700",
                        borderRadius: "8px",
                        fontSize: "12px"
                      }}
                      title="Delete"
                    >
                      <Icon icon="solar:trash-bin-trash-bold" className="text-md" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {ads.length === 0 && !loading && (
            <div className="col-12">
              <div className="text-center py-80 bg-white radius-16 border border-dashed">
                <div className="bg-light-50 w-80-px h-80-px radius-circle d-flex align-items-center justify-content-center mx-auto mb-16 shadow-sm">
                  <Icon icon="solar:gallery-wide-bold" className="text-4xl text-secondary-light" />
                </div>
                <h5 className="text-dark fw-bold mb-4">No active mobile ads.</h5>
                <p className="text-secondary-light mb-0">Start by creating a new banner ad with an expiry date.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ad-preview-card:hover img {
          transform: scale(1.03);
        }
        .ad-preview-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(0,0,0,0.05);
        }
        .hover-scale-105:hover:not(:disabled) {
          transform: scale(1.03);
          opacity: 0.95;
        }
      `}</style>
    </div>
  );
};

export default AdLayer;
